
// Build base paths once
const BASE = {
    utility:  "static/assets/icons/utility-sprite/svg/symbols.svg",
    standard: "static/assets/icons/standard-sprite/svg/symbols.svg",
    action:   "static/assets/icons/action-sprite/svg/symbols.svg",
    custom:   "static/assets/icons/custom-sprite/svg/symbols.svg",
    doctype:  "static/assets/icons/doctype-sprite/svg/symbols.svg",
};

// 1) TEMPLATE-FRIENDLY MAP: {iconMap.utility.edit}
export const iconMap = new Proxy({}, {
    get(_, cat) {
        const base = BASE[String(cat)];
        if (!base) return undefined;
        return new Proxy({}, {
            get(_, name) {
                return `${base}#${String(name)}`;
            }
        });
    }
});


// Create the  path to the icon  --> e.g.) iconPath('standard:account', false)
export function iconPath(iconStr, isContainer = false, isInputIcon = false) {
    if (!iconStr) return;

    // allow "utility:edit" or just "edit" (defaults to utility)
    let category = 'utility';
    let name = iconStr;

    if (iconStr.includes(':')) {
        const parts = iconStr.split(':');
        category = (parts[0] || 'utility').trim().toLowerCase();
        name = (parts[1] || '').trim();
    } else {
        name = (iconStr || '').trim();
    }

    if (!BASE[category]) category = 'utility';

    // href for <use>
    const iconHref = `${BASE[category]}#${name}`;

    // SLDS container class wants hyphens
    const iconNoUnderScore = name.includes('_') ? name.replaceAll('_', '-') : name;

    if (!isContainer) return iconHref;

    // keep your old class behavior
    const baseCls = `slds-icon_container slds-icon-${category}-${iconNoUnderScore}`;
    const inputAddon = isInputIcon ? ' slds-input__icon slds-input__icon_left' : '';
    const utilityTint = category === 'utility' ? ' slds-current-color' : '';

    return {
        container: `${baseCls}${inputAddon}${utilityTint}`,
        icon: iconHref
    };
}

function getIconBase(icon) {
    if (!icon) return [null, null];
    if (typeof icon  === 'string' && icon?.split(':')?.length === 2 ) {
        const [category, value] = icon.split(':');
        return [category, value];
    }
    if (typeof icon === 'object') {
        let iconName;
        if (icon.formula && typeof icon.formula === 'function') {
            iconName = icon.formula(icon);
        } else {
            iconName = icon.icon;
        }
        if (!iconName.split(':')?.length === 2) {
            return [null, null];
        }
        const [category, value] = iconName.split(':');
        return [category, value];
    }
    return [null, null];
}
function getIconAttributes(icon) {
     
    if (typeof icon !== 'object') {
        return {sizeCl: '', iconCl: '', iconTitle: ''};
    }

    return {
        sizeCl: icon?.size ? `slds-icon_${icon.size}` : '', 
        iconCl: icon?.iconCl ?? '',
        iconTitle: icon?.iconTitle ?? ''
    };
}


export function iconPathV2(icon) {
    if (!icon) return;
    const [category, name] = getIconBase(icon);
    const {sizeCl, iconCl, iconTitle} = getIconAttributes(icon);
    const iconConfig = iconPath(`${category}:${name}`, true);

    if (!iconConfig) {
        return;
    }
    
    iconConfig.svg = `slds-icon ${sizeCl ?? ''} ${iconCl ?? 'slds-current-color'}`;
    iconConfig.iconTitle = iconTitle ?? '';
    return iconConfig;

}


// Get the Icon for an Object Api Name
export function iconByObjectApiNameV2(objectApiName, customClassStr=null, isSvg=false) {
    const icons = {
        Inspection__c: {iconName: "standard:policy", iconClass: "", title: 'Inspection'},
        Testing__c: { iconName: "standard:data_governance", iconClass: "", title: 'Test' },
        FAT__c: { iconName: "standard:data_governance", iconClass: "", title: 'FAT' },
        Service__c: { iconName: "standard:service_request", iconClass: "service-icon", title: 'Service'},
        Component__c: { iconName: "custom:custom19", iconClass: "", title: 'Component' },
        Parts_and_Services__c: { iconName: "custom:custom19", iconClass: "", title: 'Component' },
        NSN_Finished_Part__c: { iconName: "standard:bundle_config", iconClass:"", title: 'NSN Finished Part' },
        RFQ__c: { iconName: "standard:partner_fund_request", iconClass: "", title: 'RFQ' },
        Account: { iconName: "standard:account", iconClass: "" , title: 'Account'},
        Contact: { iconName: "standard:contact", iconClass: "" , title: 'Contact'},
        RFQ_Line_Item__c: { iconName: "standard:list_fee", iconClass: "" , title: 'RFQ Line Item'},
        Opportunity: {iconName: "standard:opportunity", iconClass: "", title: 'Opportunity'},
        Lead: {iconName: "standard:lead", iconClass: "lead-icon", title: 'Lead'},
        Contract_Award__c: {iconName: "standard:contract", iconClass: "", title: 'Contract Award'},
        Capabilities__c: {iconName: "standard:skill_entity", iconClass: "", title: 'Capabilities'},
        Casting__c: {iconName: "standard:skill", iconClass: "", title: 'Casting'},
        Forging__c: {iconName: "standard:skill", iconClass: "", title: 'Forging'},
        Machining__c: {iconName: "standard:skill", iconClass: "", title: 'Machining'},
        Task: {iconName: "standard:task", iconClass: "", title: "Task"},
    };

    let iconConfig = {};

    const handleObjApiName = () => {
        if (objectApiName) {
            return icons.hasOwnProperty(objectApiName) 
                ? icons[objectApiName] 
                    : objectApiName.endsWith('__c') 
                ? {iconName: 'standard:custom', iconClass: '', title: getCleanMetaDataLabels(objectApiName)}
                : {iconName: 'standard:empty', iconClass: '', title: 'Empty'};
        } 
        return {iconName: 'standard:empty', iconClass: '', title: 'Empty'};
    }

    if (isSvg) {
        const iconProps = handleObjApiName();
        // Href & <span> container 
        const svgIcon = iconPath(iconProps.iconName, true);
        return svgIcon;

    } else {
        
        iconConfig = handleObjApiName();

        if (customClassStr) {
            // Handle Cutom Class
            iconConfig.iconClass = `${iconConfig.iconClass} ${customClassStr}`.trim();
        }    
    }

    

    return iconConfig
}

export function passIcons(iconStr) {
    const parts = iconStr.split(':');
    if (parts.length = 3) {
        const [category, icon, size] = parts;
        const sizeCl = size === 'medium' ? '' : `slds-icon_${size}`;
        return {
            container: `slds-icon_container slds-icon-${category}-${icon}`,
            svg: `slds-icon ${sizeCl}`,
            icon: `static/assets/icons/${category}-sprite/svg/symbols.svg#${icon}`
        }
    } else if (parts.length === 2) {
        const [category, icon] = parts;
        return {
            container: `slds-icon_container slds-icon-${category}-${icon}`,
            svg: `slds-icon`,
            icon: `static/assets/icons/${category}-sprite/svg/symbols.svg#${icon}`
        }
    }
    return null;
}


export const MATERIAL_ICONS = {
    house: {
        path: "M200-160v-366L88-440l-48-64 440-336 160 122v-82h120v174l160 122-48 64-112-86v366H520v-240h-80v240H200Zm80-80h80v-240h240v240h80v-347L480-739 280-587v347Zm120-319h160q0-32-24-52.5T480-632q-32 0-56 20.5T400-559Zm-40 319v-240h240v240-240H360v240Z",
        viewBox: "0 -960 960 960"
    },
    home_mode: {
        path: "M480-510ZM160-160v-375l-72 55-47-63 439-337 440 336-48 64-392-300-240 184v356h120v80H160Zm437 80L428-250l56-57 113 113 227-226 56 57L597-80Z",
        viewBox: "0 -960 960 960"
    },
    water_drop: {
        path: "M491-200q12-1 20.5-9.5T520-230q0-14-9-22.5t-23-7.5q-41 3-87-22.5T343-375q-2-11-10.5-18t-19.5-7q-14 0-23 10.5t-6 24.5q17 91 80 130t127 35Zm-239.5 26Q160-268 160-408q0-100 79.5-217.5T480-880q161 137 240.5 254.5T800-408q0 140-91.5 234T480-80q-137 0-228.5-94ZM652-230.5Q720-301 720-408q0-73-60.5-165T480-774Q361-665 300.5-573T240-408q0 107 68 177.5T480-160q104 0 172-70.5ZM480-480Z",
        viewBox: "0 -960 960 960",
    }, 
    outdoor: {
        path: "M440-760v-160h80v160h-80Zm266 110-55-55 112-115 56 57-113 113Zm54 210v-80h160v80H760ZM440-40v-160h80v160h-80ZM254-652 140-763l57-56 113 113-56 54Zm508 512L651-255l54-54 114 110-57 59ZM40-440v-80h160v80H40Zm157 300-56-57 112-112 29 27 29 28-114 114Zm113-170q-70-70-70-170t70-170q70-70 170-70t170 70q70 70 70 170t-70 170q-70 70-170 70t-170-70Zm283-57q47-47 47-113t-47-113q-47-47-113-47t-113 47q-47 47-47 113t47 113q47 47 113 47t113-47ZM480-480Z",
        viewBox: "0 -960 960 960",
    },
    restroom: {
        path: "M220-80v-300h-60v-220q0-33 23.5-56.5T240-680h120q33 0 56.5 23.5T440-600v220h-60v300H220Zm23.5-663.5Q220-767 220-800t23.5-56.5Q267-880 300-880t56.5 23.5Q380-833 380-800t-23.5 56.5Q333-720 300-720t-56.5-23.5ZM600-80v-240H480l102-306q8-26 29.5-40t48.5-14q27 0 48.5 14t29.5 40l102 306H720v240H600Zm3.5-663.5Q580-767 580-800t23.5-56.5Q627-880 660-880t56.5 23.5Q740-833 740-800t-23.5 56.5Q693-720 660-720t-56.5-23.5Z",
        viewBox: "0 -960 960 960",
    },
    chlorine: {
        path: "M491-200q12-1 20.5-9.5T520-230q0-14-9-22.5t-23-7.5q-41 3-87-22.5T343-375q-2-11-10.5-18t-19.5-7q-14 0-23 10.5t-6 24.5q17 91 80 130t127 35Zm-239.5 26Q160-268 160-408q0-100 79.5-217.5T480-880q161 137 240.5 254.5T800-408q0 140-91.5 234T480-80q-137 0-228.5-94ZM652-230.5Q720-301 720-408q0-73-60.5-165T480-774Q361-665 300.5-573T240-408q0 107 68 177.5T480-160q104 0 172-70.5ZM480-480Z",
        viewBox: "0 -960 960 960",
        text: 'CL'
    },
    salinity: {
        path: "M251.5-174Q160-268 160-408h80q0 107 68 177.5T480-160q104 0 172-70.5T720-408h80q0 140-91.5 234T480-80q-137 0-228.5-94Zm-80-317.5Q160-503 160-520t11.5-28.5Q183-560 200-560t28.5 11.5Q240-537 240-520t-11.5 28.5Q217-480 200-480t-28.5-11.5Zm180 0Q340-503 340-520t11.5-28.5Q363-560 380-560t28.5 11.5Q420-537 420-520t-11.5 28.5Q397-480 380-480t-28.5-11.5Zm200 0Q540-503 540-520t11.5-28.5Q563-560 580-560t28.5 11.5Q620-537 620-520t-11.5 28.5Q597-480 580-480t-28.5-11.5Zm180 0Q720-503 720-520t11.5-28.5Q743-560 760-560t28.5 11.5Q800-537 800-520t-11.5 28.5Q777-480 760-480t-28.5-11.5Zm-470-110Q250-613 250-630t11.5-28.5Q273-670 290-670t28.5 11.5Q330-647 330-630t-11.5 28.5Q307-590 290-590t-28.5-11.5Zm380 0Q630-613 630-630t11.5-28.5Q653-670 670-670t28.5 11.5Q710-647 710-630t-11.5 28.5Q687-590 670-590t-28.5-11.5Zm-190-10Q440-623 440-640t11.5-28.5Q463-680 480-680t28.5 11.5Q520-657 520-640t-11.5 28.5Q497-600 480-600t-28.5-11.5Zm-100-100Q340-723 340-740t11.5-28.5Q363-780 380-780t28.5 11.5Q420-757 420-740t-11.5 28.5Q397-700 380-700t-28.5-11.5Zm200 0Q540-723 540-740t11.5-28.5Q563-780 580-780t28.5 11.5Q620-757 620-740t-11.5 28.5Q597-700 580-700t-28.5-11.5Zm-100-100Q440-823 440-840t11.5-28.5Q463-880 480-880t28.5 11.5Q520-857 520-840t-11.5 28.5Q497-800 480-800t-28.5-11.5ZM480-408Z",
        viewBox: "0 -960 960 960",
    },
    dashboard: {
        path: "M520-600v-240h320v240H520ZM120-440v-400h320v400H120Zm400 320v-400h320v400H520Zm-400 0v-240h320v240H120Zm80-400h160v-240H200v240Zm400 320h160v-240H600v240Zm0-480h160v-80H600v80ZM200-200h160v-80H200v80Zm160-320Zm240-160Zm0 240ZM360-280Z",
        viewBox: "0 -960 960 960",
    },
    listings: {
        path: "M360-240h440v-107H360v107ZM160-613h120v-107H160v107Zm0 187h120v-107H160v107Zm0 186h120v-107H160v107Zm200-186h440v-107H360v107Zm0-187h440v-107H360v107ZM160-160q-33 0-56.5-23.5T80-240v-480q0-33 23.5-56.5T160-800h640q33 0 56.5 23.5T880-720v480q0 33-23.5 56.5T800-160H160Z",
        viewBox: "0 -960 960 960",
    }, 
    calendar: {
        path: "M200-80q-33 0-56.5-23.5T120-160v-560q0-33 23.5-56.5T200-800h40v-80h80v80h320v-80h80v80h40q33 0 56.5 23.5T840-720v560q0 33-23.5 56.5T760-80H200Zm0-80h560v-400H200v400Zm0-480h560v-80H200v80Zm0 0v-80 80Zm280 240q-17 0-28.5-11.5T440-440q0-17 11.5-28.5T480-480q17 0 28.5 11.5T520-440q0 17-11.5 28.5T480-400Zm-188.5-11.5Q280-423 280-440t11.5-28.5Q303-480 320-480t28.5 11.5Q360-457 360-440t-11.5 28.5Q337-400 320-400t-28.5-11.5ZM640-400q-17 0-28.5-11.5T600-440q0-17 11.5-28.5T640-480q17 0 28.5 11.5T680-440q0 17-11.5 28.5T640-400ZM480-240q-17 0-28.5-11.5T440-280q0-17 11.5-28.5T480-320q17 0 28.5 11.5T520-280q0 17-11.5 28.5T480-240Zm-188.5-11.5Q280-263 280-280t11.5-28.5Q303-320 320-320t28.5 11.5Q360-297 360-280t-11.5 28.5Q337-240 320-240t-28.5-11.5ZM640-240q-17 0-28.5-11.5T600-280q0-17 11.5-28.5T640-320q17 0 28.5 11.5T680-280q0 17-11.5 28.5T640-240Z",
        viewBox: "0 -960 960 960",
    },
    reservations: {
        path: "M438-226 296-368l58-58 84 84 168-168 58 58-226 226ZM200-80q-33 0-56.5-23.5T120-160v-560q0-33 23.5-56.5T200-800h40v-80h80v80h320v-80h80v80h40q33 0 56.5 23.5T840-720v560q0 33-23.5 56.5T760-80H200Zm0-80h560v-400H200v400Zm0-480h560v-80H200v80Zm0 0v-80 80Z",
        viewBox: "0 -960 960 960",
    },
    messages: {
        path: "M160-160q-33 0-56.5-23.5T80-240v-480q0-33 23.5-56.5T160-800h640q33 0 56.5 23.5T880-720v480q0 33-23.5 56.5T800-160H160Zm320-280L160-640v400h640v-400L480-440Zm0-80 320-200H160l320 200ZM160-640v-80 480-400Z",
        viewBox: "0 -960 960 960",
    },
    accountSettings: {
        path:"M200-246q54-53 125.5-83.5T480-360q83 0 154.5 30.5T760-246v-514H200v514Zm379-235q41-41 41-99t-41-99q-41-41-99-41t-99 41q-41 41-41 99t41 99q41 41 99 41t99-41ZM200-120q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h560q33 0 56.5 23.5T840-760v560q0 33-23.5 56.5T760-120H200Zm69-80h422q-44-39-99.5-59.5T480-280q-56 0-112.5 20.5T269-200Zm168.5-337.5Q420-555 420-580t17.5-42.5Q455-640 480-640t42.5 17.5Q540-605 540-580t-17.5 42.5Q505-520 480-520t-42.5-17.5ZM480-503Z",
        viewBox: "0 -960 960 960",
    },
    payments: {
        path: "M560-440q-50 0-85-35t-35-85q0-50 35-85t85-35q50 0 85 35t35 85q0 50-35 85t-85 35ZM280-320q-33 0-56.5-23.5T200-400v-320q0-33 23.5-56.5T280-800h560q33 0 56.5 23.5T920-720v320q0 33-23.5 56.5T840-320H280Zm80-80h400q0-33 23.5-56.5T840-480v-160q-33 0-56.5-23.5T760-720H360q0 33-23.5 56.5T280-640v160q33 0 56.5 23.5T360-400Zm440 240H120q-33 0-56.5-23.5T40-240v-440h80v440h680v80ZM280-400v-320 320Z",
        viewBox: "0 -960 960 960",
    },
    identity: {
        path: "M381-481q-41-41-41-99t41-99q41-41 99-41t99 41q41 41 41 99t-41 99q-41 41-99 41t-99-41Zm141.5-56.5Q540-555 540-580t-17.5-42.5Q505-640 480-640t-42.5 17.5Q420-605 420-580t17.5 42.5Q455-520 480-520t42.5-17.5ZM480-60 120-280v-400l360-220 360 220v400L480-60Zm0-93 147-91q-34-18-71.5-27t-75.5-9q-38 0-75.5 9T333-244l147 91ZM256-291q50-34 107-51.5T480-360q60 0 117 17.5T704-291l56-33v-311L480-806 200-635v311l56 33Zm224-189Z",
        viewBox: "0 -960 960 960",
    },
    poolGeneral: {
        path: "M80-120v-80q38 0 57-20t75-20q56 0 77 20t57 20q36 0 57-20t77-20q56 0 77 20t57 20q36 0 57-20t77-20q56 0 75 20t57 20v80q-59 0-77.5-20T748-160q-36 0-57 20t-77 20q-56 0-77-20t-57-20q-36 0-57 20t-77 20q-56 0-77-20t-57-20q-36 0-54.5 20T80-120Zm0-180v-80q38 0 57-20t75-20q56 0 77.5 20t56.5 20q36 0 57-20t77-20q56 0 77 20t57 20q36 0 57-20t77-20q56 0 75 20t57 20v80q-59 0-77.5-20T748-340q-36 0-55.5 20T614-300q-57 0-77.5-20T480-340q-38 0-56.5 20T346-300q-59 0-78.5-20T212-340q-36 0-54.5 20T80-300Zm196-204 133-133-40-40q-33-33-70-48t-91-15v-100q75 0 124 16.5t96 63.5l256 256q-17 11-33 17.5t-37 6.5q-36 0-57-20t-77-20q-56 0-77 20t-57 20q-21 0-37-6.5T276-504Zm463-306.5q29 29.5 29 70.5 0 42-29 71t-71 29q-42 0-71-29t-29-71q0-41 29-70.5t71-29.5q42 0 71 29.5Z",
        viewBox: "0 -960 960 960",
    }, 
    location: {
        path: "M536.5-503.5Q560-527 560-560t-23.5-56.5Q513-640 480-640t-56.5 23.5Q400-593 400-560t23.5 56.5Q447-480 480-480t56.5-23.5ZM480-186q122-112 181-203.5T720-552q0-109-69.5-178.5T480-800q-101 0-170.5 69.5T240-552q0 71 59 162.5T480-186Zm0 106Q319-217 239.5-334.5T160-552q0-150 96.5-239T480-880q127 0 223.5 89T800-552q0 100-79.5 217.5T480-80Zm0-480Z",
        viewBox: "0 -960 960 960",
    },
    location_city: {
        path: "M120-120v-560h240v-80l120-120 120 120v240h240v400H120Zm80-80h80v-80h-80v80Zm0-160h80v-80h-80v80Zm0-160h80v-80h-80v80Zm240 320h80v-80h-80v80Zm0-160h80v-80h-80v80Zm0-160h80v-80h-80v80Zm0-160h80v-80h-80v80Zm240 480h80v-80h-80v80Zm0-160h80v-80h-80v80Z",
        viewBox: "0 -960 960 960",
    },
    info: {
        path: "M440-280h80v-240h-80v240Zm68.5-331.5Q520-623 520-640t-11.5-28.5Q497-680 480-680t-28.5 11.5Q440-657 440-640t11.5 28.5Q463-600 480-600t28.5-11.5ZM480-80q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm0-320Z",
        viewBox: "0 -960 960 960",
    },
    basketball: {
        path: "M162-520h114q-6-38-23-71t-43-59q-18 29-30.5 61.5T162-520Zm522 0h114q-5-36-17.5-68.5T750-650q-26 26-43 59t-23 71ZM210-310q26-26 43-59t23-71H162q5 36 17.5 68.5T210-310Zm540 0q18-29 30.5-61.5T798-440H684q6 38 23 71t43 59ZM358-520h82v-278q-53 8-98.5 29.5T260-712q39 38 64.5 86.5T358-520Zm162 0h82q8-57 33.5-105.5T700-712q-36-35-81.5-56.5T520-798v278Zm-80 358v-278h-82q-8 57-33.5 105.5T260-248q36 35 81.5 56.5T440-162Zm80 0q53-8 98.5-29.5T700-248q-39-38-64.5-86.5T602-440h-82v278Zm-40-318Zm0 400q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Z",
        viewBox: "0 -960 960 960"
    },
    volleyball: {
        path: "M782-582q-31-87-101-145.5T520-798v64l262 152ZM320-434l120-70v-294q-32 3-62 14.5T320-756v322Zm-134 78 54-32v-302q-39 44-59.5 98T160-480q0 32 6.5 63.5T186-356Zm134 152 280-160-120-70-254 148q20 25 43 46t51 36Zm160 44q75 0 142-34t112-94l-54-30-264 152q16 3 32 4.5t32 1.5Zm294-196q13-29 19.5-60.5T800-480L520-642v138l254 148ZM480-480Zm0 400q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Z",
        viewBox: "0 -960 960 960"
    },
    grill: {
        path: "M640-320q50 0 85 35t35 85q0 50-35 85t-85 35q-38 0-68.5-22T528-160H274l-40 62q-9 14-25.5 17.5T178-86q-14-9-17.5-25.5T166-142l158-242q-72-33-118-101.5T160-640h560q0 86-46 154.5T556-384l23 36q-21 10-34.5 20T515-300l-40-62q-8 2-17 2h-36q-9 0-17-2l-79 122h202q13-36 43.5-58t68.5-22Zm28.5 148.5Q680-183 680-200t-11.5-28.5Q657-240 640-240t-28.5 11.5Q600-217 600-200t11.5 28.5Q623-160 640-160t28.5-11.5ZM440-440q60 0 109.5-33t74.5-87H256q26 54 75 87t109 33ZM336-680q5-29-1.5-49T307-775q-20-26-26.5-49.5T279-880h40q-5 29 1.5 48.5T348-786q21 26 27 49.5t1 56.5h-40Zm100 0q5-29-1-49t-27-46q-21-25-27.5-48.5T379-880h40q-5 29 1.5 48.5T448-786q20 25 26.5 48.5T476-680h-40Zm100 0q5-29-1-49t-27-46q-21-25-27.5-48.5T479-880h40q-5 29 1.5 48.5T548-786q20 25 26.5 48.5T576-680h-40Zm-96 240Z",
        viewBox: "0 -960 960 960",
    },
    heat: {
        path: "M532-131q-6 5-12.5 8t-14.5 3q-8 0-16-3.5t-14-9.5q-41-44-60.5-90T395-320q0-37 11-78t38-106q23-57 32-87.5t9-56.5q0-34-15-63.5T423-771q-6-6-9.5-14t-3.5-16q0-8 3-14.5t8-12.5q6-6 13.5-9t15.5-3q8 0 15 3t13 8q44 41 65.5 86t21.5 95q0 35-10.5 73.5T518-474q-25 60-34 92t-9 61q0 35 14.5 67.5T534-188q5 6 8 13t3 15q0 8-3 15.5T532-131Zm195 0q-6 5-12.5 8t-14.5 3q-8 0-16-3.5t-14-9.5q-41-44-60.5-89.5T590-319q0-37 11-79t38-106q23-57 32-87t9-56q0-34-15-64.5T618-771q-6-6-9-13.5t-3-15.5q0-8 2.5-14.5T616-827q6-6 14-9.5t16-3.5q8 0 14.5 3t12.5 8q44 41 65.5 86t21.5 95q0 35-10.5 73.5T713-473q-25 60-34 92t-9 60q0 35 15 68.5t45 65.5q5 6 7.5 13t2.5 14q0 8-3 16t-10 13Zm-390 0q-6 5-12.5 8t-14.5 3q-8 0-16-3.5t-14-9.5q-41-44-60.5-89.5T200-319q0-37 11-79t38-106q23-57 32-87t9-56q0-34-15-64.5T228-771q-7-6-10-13.5t-3-15.5q0-8 3-15t8-13q6-6 13.5-9t15.5-3q8 0 15 3t13 8q44 41 65.5 85.5T370-648q0 35-10 73.5T324-474q-25 60-34 92t-9 61q0 35 14.5 68.5T340-187q5 6 7.5 13t2.5 14q0 8-3 16t-10 13Z",
        viewBox: "0 -960 960 960",
    },
    water_heater: {
        path: "M372-529q0 19 6.5 37t18.5 33q2-12 8-23.5t15-19.5l60-58 59 59q9 8 15 19t8 23q11-15 19.5-32t8.5-36q0-19-6-36.5T566-596q-11 5-22.5 8t-23.5 3q-30 0-55-17t-38-45q-12 12-22 25.5T387.5-593q-7.5 15-11.5 31t-4 33Zm108 53-17 17q-4 4-5.5 8t-1.5 9q0 10 7 16t17 6q10 0 17-6t7-16q0-5-1.5-9t-5.5-8l-17-17Zm0-284v76q0 17 12 28.5t29 11.5q11 0 20-6.5t16-15.5l7-10q41 23 63.5 62.5T650-527q0 70-50 118.5T480-360q-70 0-119-49t-49-119q0-77 49-137t119-95ZM240-80q-33 0-56.5-23.5T160-160v-560q0-66 47-113t113-47h320q66 0 113 47t47 113v560q0 33-23.5 56.5T720-80H240Zm0-160v80h480v-80q-30 0-48 20t-72 20q-54 0-70.5-20T480-240q-33 0-49.5 20T360-200q-54 0-70.5-20T240-240Zm120-40q33 0 49.5-20t70.5-20q54 0 72 20t48 20q30 0 48-20t72-20v-400q0-33-23.5-56.5T640-800H320q-33 0-56.5 23.5T240-720v400q54 0 70.5 20t49.5 20Z",
        viewBox: "0 -960 960 960",
    },
    bluetooth_speakers: {
        path: "M167-167q-47-47-47-113t47-113q47-47 113-47 23 0 42.5 5.5T360-418v-422h240v160H440v400q0 66-47 113t-113 47q-66 0-113-47Zm513 47v-182L570-192l-34-34 134-134-136-134 34-34 110 110v-182h24l138 138-104 102 104 102-136 138h-24Zm46-92 46-46-46-44v90Zm0-206 46-44-46-46v90Z",
        viewBox: "0 -960 960 960"
    },
    sauna: {
        path: "M160-80q-33 0-56.5-23.5T80-160v-640q0-33 23.5-56.5T160-880h640q33 0 56.5 23.5T880-800v640q0 33-23.5 56.5T800-80H160Zm0-240v160h80v-80h-40v-80h-40Zm0-80h40q0-17 11.5-28.5T240-440v-100q0-25 17.5-42.5T300-600h80q25 0 42.5 17.5T440-540v100q17 0 28.5 11.5T480-400h320v-400H160v400Zm100 100h160v-80H260v80Zm80-340q-25 0-42.5-17.5T280-700q0-25 17.5-42.5T340-760q25 0 42.5 17.5T400-700q0 25-17.5 42.5T340-640Zm-20 480h40v-80h-40v80Zm120 0h360v-160H480v80h-40v80Zm50-400q2-13 2.5-19.5t.5-13.5q0-11-4.5-21T470-641q-15-19-22.5-38t-7.5-39q0-8 1-16.5l3-25.5h60q-2 11-3 20.5t-1 21.5q0 11 4.5 21.5T520-674q18 22 26 41.5t8 39.5q0 11-1 18t-3 15h-60Zm100 0q2-13 2.5-19.5t.5-13.5q0-11-4.5-21T570-641q-15-19-22.5-38t-7.5-39q0-8 1-16.5l3-25.5h60q-2 11-3 20.5t-1 21.5q0 11 4.5 21.5T620-674q18 22 26 41.5t8 39.5q0 11-1 18t-3 15h-60Zm102 0q2-13 2.5-19.5t.5-13.5q0-11-4.5-21T672-641q-15-19-22.5-38t-7.5-39q0-8 1-16.5l3-25.5h60q-2 11-3 20.5t-1 21.5q0 11 4.5 21.5T722-674q18 22 26 41.5t8 39.5q0 11-1 18t-3 15h-60ZM260-300v-80 80Z",
        viewBox: "0 -960 960 960"
    },
    wheelchair: {
        path: "M423.5-743.5Q400-767 400-800t23.5-56.5Q447-880 480-880t56.5 23.5Q560-833 560-800t-23.5 56.5Q513-720 480-720t-56.5-23.5ZM680-80v-200H480q-33 0-56.5-23.5T400-360v-240q0-33 23.5-56.5T480-680q24 0 41.5 10.5T559-636q55 66 99.5 90.5T760-520v80q-53 0-107-23t-93-55v138h120q33 0 56.5 23.5T760-300v220h-80Zm-280 0q-83 0-141.5-58.5T200-280q0-72 45.5-127T360-476v82q-35 14-57.5 44.5T280-280q0 50 35 85t85 35q39 0 69.5-22.5T514-240h82q-14 69-69 114.5T400-80Z",
        viewBox: "0 -960 960 960"
    },
    accessibility: {
        path: "M423.5-743.5Q400-767 400-800t23.5-56.5Q447-880 480-880t56.5 23.5Q560-833 560-800t-23.5 56.5Q513-720 480-720t-56.5-23.5ZM360-80v-520H120v-80h720v80H600v520h-80v-240h-80v240h-80Z",
        viewBox: "0 -960 960 960"
    },
    kitchen: {
        path: "M160-160v-320H80v-80h160q-33 0-56.5-23.5T160-640v-160h240v160q0 33-23.5 56.5T320-560h320v-120q0-17-11.5-28.5T600-720q-17 0-28.5 11.5T560-680h-80q0-50 35-85t85-35q50 0 85 35t35 85v120h160v80h-80v320H160Zm80-480h80v-80h-80v80Zm0 400h200v-240H240v240Zm280 0h200v-240H520v240ZM240-640h80-80Zm0 400h480-480Z",
        viewBox: "0 -960 960 960",
    },
    refigerator: {
        path: "M320-640v-120h80v120h-80Zm0 360v-200h80v200h-80ZM240-80q-33 0-56.5-23.5T160-160v-640q0-33 23.5-56.5T240-880h480q33 0 56.5 23.5T800-800v640q0 33-23.5 56.5T720-80H240Zm0-80h480v-360H240v360Zm0-440h480v-200H240v200Z",
        viewBox: "0 -960 960 960"   
    },
    dining_table: {
        path: "M160-160q-33 0-56.5-23.5T80-240v-440h80v440h280v80H160Zm120-560q-33 0-56.5-23.5T200-800q0-33 23.5-56.5T280-880q33 0 56.5 23.5T360-800q0 33-23.5 56.5T280-720ZM480-80v-200H280q-33 0-56.5-23.5T200-360v-236q0-35 24-59.5t58-24.5q19 0 35.5 8t28.5 22q45 49 96.5 89.5T560-520h54q-25-17-39.5-42.5T560-620h241q0 32-14.5 57.5T747-520h133v80H720v360h-80v-360h-80q-53 0-107-23t-93-55v138h120q33 0 56.5 23.5T560-300v220h-80Z",
        viewBox: "0 -960 960 960"
    },
    beach_umbrella: {
        path: "M784-120 530-374l56-56 254 254-56 56Zm-546-28q-60-60-89-135t-29-153q0-78 29-152t89-134q60-60 134.5-89.5T525-841q78 0 152.5 29.5T812-722L238-148Zm8-122 54-54q-16-21-30.5-43T243-411q-12-22-21-44t-16-43q-11 59-1.5 118T246-270Zm112-110 222-224q-43-33-86.5-53.5t-81.5-28q-38-7.5-68.5-2.5T296-666q-17 18-22 48.5t2.5 69q7.5 38.5 28 81.5t53.5 87Zm278-280 56-54q-53-32-112-42t-118 2q22 7 44 16t44 20.5q22 11.5 43.5 26T636-660Z",
        viewBox: "0 -960 960 960"
    },
    cabana: {
        path: "M200-120v-160h-80v-80h80v-166L88-440l-48-64 440-336 440 336-48 64-112-86v166h80v80h-80v160h-80v-160H520v160h-80v-160H280v160h-80Zm80-240h160v-349L280-587v227Zm240 0h160v-227L520-709v349Z",
        viewBox: "0 -960 960 960"
    },
    shower: {
        path: "M320-240q-17 0-28.5-11.5T280-280q0-17 11.5-28.5T320-320q17 0 28.5 11.5T360-280q0 17-11.5 28.5T320-240Zm160 0q-17 0-28.5-11.5T440-280q0-17 11.5-28.5T480-320q17 0 28.5 11.5T520-280q0 17-11.5 28.5T480-240Zm160 0q-17 0-28.5-11.5T600-280q0-17 11.5-28.5T640-320q17 0 28.5 11.5T680-280q0 17-11.5 28.5T640-240ZM200-400v-80q0-106 68-184t172-92v-84h80v84q104 14 172 92t68 184v80H200Zm80-80h400q0-83-58.5-141.5T480-680q-83 0-141.5 58.5T280-480Zm40 360q-17 0-28.5-11.5T280-160q0-17 11.5-28.5T320-200q17 0 28.5 11.5T360-160q0 17-11.5 28.5T320-120Zm160 0q-17 0-28.5-11.5T440-160q0-17 11.5-28.5T480-200q17 0 28.5 11.5T520-160q0 17-11.5 28.5T480-120Zm160 0q-17 0-28.5-11.5T600-160q0-17 11.5-28.5T640-200q17 0 28.5 11.5T680-160q0 17-11.5 28.5T640-120ZM480-480Z",
        viewBox: "0 -960 960 960"
    },
    celebration: {
        path: "m80-80 200-560 360 360L80-80Zm132-132 282-100-182-182-100 282Zm370-246-42-42 224-224q32-32 77-32t77 32l24 24-42 42-24-24q-14-14-35-14t-35 14L582-458ZM422-618l-42-42 24-24q14-14 14-34t-14-34l-26-26 42-42 26 26q32 32 32 76t-32 76l-24 24Zm80 80-42-42 144-144q14-14 14-35t-14-35l-64-64 42-42 64 64q32 32 32 77t-32 77L502-538Zm160 160-42-42 64-64q32-32 77-32t77 32l64 64-42 42-64-64q-14-14-35-14t-35 14l-64 64ZM212-212Z",
        viewBox: "0 -960 960 960"
    },
    baby_changing_station: {
        path: "M120-80v-400l63-185q8-26 30-40.5t47-14.5q8 0 16 1.5t16 5.5l166 73h102v80H440l-108-47-52 157v370H120Zm240-120v-80h480v80H360Zm420-120q-25 0-42.5-17.5T720-380q0-25 17.5-42.5T780-440q25 0 42.5 17.5T840-380q0 25-17.5 42.5T780-320Zm-260 0q-33 0-56.5-23.5T440-400v-40h-80v-80h120q17 0 28.5 11.5T520-480v40h80v-80h80v120q0 33-23.5 56.5T600-320h-80ZM320-760q-33 0-56.5-23.5T240-840q0-33 23.5-56.5T320-920q33 0 56.5 23.5T400-840q0 33-23.5 56.5T320-760Z",
        viewBox: "0 -960 960 960"
    },
    television: {
        path: "M320-120v-80H160q-33 0-56.5-23.5T80-280v-480q0-33 23.5-56.5T160-840h640q33 0 56.5 23.5T880-760v480q0 33-23.5 56.5T800-200H640v80H320ZM160-280h640v-480H160v480Zm0 0v-480 480Z",
        viewBox: "0 -960 960 960"
    },
    children: {
        path: "M297-97q-17-17-17-43t17-43q17-17 43-17t43 17q17 17 17 43t-17 43q-17 17-43 17t-43-17Zm440 0q-17-17-17-43t17-43q17-17 43-17t43 17q17 17 17 43t-17 43q-17 17-43 17t-43-17ZM540-656 384-844q36-18 78-27t90-9q51 0 97.5 14.5T732-824L540-656Zm-60 416q-83 0-141.5-58.5T280-440v-308l-20-24q-14-17-23.5-22.5T216-800q-24 0-40 18t-16 42H80q0-57 39.5-98.5T216-880q30 0 55 14t51 44l428 516q17 20 6.5 43T720-240H480Zm0-80h154L360-652v212q0 50 35 85t85 35Zm17-166Z",
        viewBox: "0 -960 960 960"
    },
    no_children: {
        path: "M240-80q-33 0-56.5-23.5T160-160q0-33 23.5-56.5T240-240q33 0 56.5 23.5T320-160q0 33-23.5 56.5T240-80Zm440-313-80-80v-141l-64 76-57-56 169-199q20-23 45-35t53-12q56 0 95 39t39 95v26h-80v-26q0-23-15.5-38.5T746-760q-10 0-19 4t-16 12l-31 35v316Zm-80 33 56 56q-11 11-25 17.5t-31 6.5H297q-26 0-36.5-23t6.5-43l108-127L56-791l56-57 736 736-57 56-360-360-47 56h216Zm40 280q-33 0-56.5-23.5T560-160q0-33 23.5-56.5T640-240q33 0 56.5 23.5T720-160q0 33-23.5 56.5T640-80ZM431-416Zm105-122Zm-100-99-57-57 55-64q-8-2-17-2h-17q-20 0-38.5 3t-37.5 8l-63-63q33-14 68-21t71-7q45 0 88.5 11t83.5 33L436-637Zm-57-57Z",
        viewBox: "0 -960 960 960"
    },
    wifi: {
        path: "M409-149q-29-29-29-71t29-71q29-29 71-29t71 29q29 29 29 71t-29 71q-29 29-71 29t-71-29ZM254-346l-84-86q59-59 138.5-93.5T480-560q92 0 171.5 35T790-430l-84 84q-44-44-102-69t-124-25q-66 0-124 25t-102 69ZM84-516 0-600q92-94 215-147t265-53q142 0 265 53t215 147l-84 84q-77-77-178.5-120.5T480-680q-116 0-217.5 43.5T84-516Z",
        viewBox: "0 -960 960 960"
    },
    no_wifi: {
        path: "M790-56 414-434q-47 11-87.5 33T254-346l-84-86q32-32 69-56t79-42l-90-90q-41 21-76.5 46.5T84-516L0-602q32-32 66.5-57.5T140-708l-84-84 56-56 736 736-58 56Zm-381-93.5Q380-179 380-220q0-42 29-71t71-29q42 0 71 29t29 71q0 41-29 70.5T480-120q-42 0-71-29.5ZM716-358l-29-29-29-29-144-144q81 8 151.5 41T790-432l-74 74Zm160-158q-77-77-178.5-120.5T480-680q-21 0-40.5 1.5T400-674L298-776q44-12 89.5-18t92.5-6q142 0 265 53t215 145l-84 86Z",
        viewBox: "0 -960 960 960"
    },
    smoking: {
        path: "M80-200v-120h600v120H80Zm640 0v-120h60v120h-60Zm100 0v-120h60v120h-60ZM720-360v-52q0-39-23-59.5T642-492h-62q-56 0-95-39t-39-95q0-56 39-95t95-39v60q-30 0-52 21t-22 53q0 32 22 53t52 21h62q56 0 97 36t41 90v66h-60Zm100 0v-90q0-66-46-114t-114-48v-60q30 0 52-22t22-52q0-30-22-52t-52-22v-60q56 0 95 39t39 95q0 29-11 52.5T754-650q56 26 91 80t35 120v90h-60Z",
        viewBox: "0 -960 960 960"
    },
    no_smoking: {
        path: "M792-56 528-320H80v-120h328L56-792l56-56 736 736-56 56Zm-38-264-34-34v-86h60v120h-26Zm66 0v-120h60v120h-60Zm-140-74-46-46h46v46Zm40-86v-52q0-39-23-59.5T642-612h-62q-56 0-95-39t-39-95q0-56 39-95t95-39v60q-30 0-52 21t-22 53q0 32 22 53t52 21h62q56 0 97 36t41 90v66h-60Zm100 0v-90q0-66-46-114t-114-48v-60q30 0 52-22t22-52h60q0 29-11 52.5T754-770q56 26 91 80t35 120v90h-60Z",
        viewBox: "0 -960 960 960"
    },
    pets: {
        path: "M180-475q-42 0-71-29t-29-71q0-42 29-71t71-29q42 0 71 29t29 71q0 42-29 71t-71 29Zm109-189q-29-29-29-71t29-71q29-29 71-29t71 29q29 29 29 71t-29 71q-29 29-71 29t-71-29Zm240 0q-29-29-29-71t29-71q29-29 71-29t71 29q29 29 29 71t-29 71q-29 29-71 29t-71-29Zm251 189q-42 0-71-29t-29-71q0-42 29-71t71-29q42 0 71 29t29 71q0 42-29 71t-71 29ZM266-75q-45 0-75.5-34.5T160-191q0-52 35.5-91t70.5-77q29-31 50-67.5t50-68.5q22-26 51-43t63-17q34 0 63 16t51 42q28 32 49.5 69t50.5 69q35 38 70.5 77t35.5 91q0 47-30.5 81.5T694-75q-54 0-107-9t-107-9q-54 0-107 9t-107 9Z",
        viewBox: "0 -960 960 960"
    },
    photos: {
        path: "M480-260q75 0 127.5-52.5T660-440q0-75-52.5-127.5T480-620q-75 0-127.5 52.5T300-440q0 75 52.5 127.5T480-260Zm0-80q-42 0-71-29t-29-71q0-42 29-71t71-29q42 0 71 29t29 71q0 42-29 71t-71 29ZM160-120q-33 0-56.5-23.5T80-200v-480q0-33 23.5-56.5T160-760h126l74-80h240l74 80h126q33 0 56.5 23.5T880-680v480q0 33-23.5 56.5T800-120H160Zm0-80h640v-480H638l-73-80H395l-73 80H160v480Zm320-240Z",
        viewBox: "0 -960 960 960"
    },
    no_photos: {
        path: "m880-195-80-80v-405H638l-73-80H395l-38 42-57-57 60-65h240l74 80h126q33 0 56.5 23.5T880-680v485Zm-720 75q-33 0-56.5-23.5T80-200v-480q0-33 23.5-56.5T160-760h41l80 80H160v480h601l80 80H160Zm466-215q-25 34-62.5 54.5T480-260q-75 0-127.5-52.5T300-440q0-46 20.5-83.5T375-586l58 58q-24 13-38.5 36T380-440q0 42 29 71t71 29q29 0 52-14.5t36-38.5l58 58Zm-18-233q25 24 38.5 57t13.5 71v12q0 6-1 12L456-619q6-1 12-1h12q38 0 71 13.5t57 38.5ZM819-28 27-820l57-57L876-85l-57 57ZM407-440Zm171-57Z",
        viewBox: "0 -960 960 960"
    },
    music: {
        path: "M447-207q-47-47-47-113t47-113q47-47 113-47 23 0 42.5 5.5T640-458v-342h240v120H720v360q0 66-47 113t-113 47q-66 0-113-47ZM80-320q0-99 38-186.5T221-659q65-65 152.5-103T560-800v80q-82 0-155 31.5t-127.5 86q-54.5 54.5-86 127T160-320H80Zm160 0q0-66 25.5-124.5t69-102Q378-590 436-615t124-25v80q-100 0-170 70t-70 170h-80Z",
        viewBox: "0 -960 960 960"
    },
    no_music: {
        path: "M792-56 56-792l56-56 736 736-56 56ZM560-514l-80-80v-246h240v160H560v166ZM400-120q-66 0-113-47t-47-113q0-66 47-113t113-47q23 0 42.5 5.5T480-418v-62l80 80v120q0 66-47 113t-113 47Z",
        viewBox: "0 -960 960 960"
    },
    alcohol: {
        path: "M240-120v-80h200v-200L120-760v-80h720v80L520-400v200h200v80H240Zm58-560h364l72-80H226l72 80Zm182 204 111-124H369l111 124Zm0 0Z"
    },
    no_alcohol: {
        path: "M240-120v-80h200v-206L56-791l56-57 736 736-57 57-271-271v126h200v80H240Zm352-362-57-55 56-63H473l-79-80h268l72-80H314l-80-80h606v80L592-482Zm-88-87Z",
        viewBox: "0 -960 960 960"
    },
    food: {
        path: "M533-440q-32-45-84.5-62.5T340-520q-56 0-108.5 17.5T147-440h386ZM40-360q0-109 91-174.5T340-600q118 0 209 65.5T640-360H40Zm0 160v-80h600v80H40ZM720-40v-80h56l56-560H450l-10-80h200v-160h80v160h200L854-98q-3 25-22 41.5T788-40h-68Zm0-80h56-56ZM80-40q-17 0-28.5-11.5T40-80v-40h600v40q0 17-11.5 28.5T600-40H80Zm260-400Z",
        viewBox: "0 -960 960 960"
    },
    no_food: {
        path: "m865-210-73-73 40-397H450l-10-80h200v-160h80v160h200l-55 550ZM625-449ZM819-28 27-820l57-57L876-85l-57 57ZM40-200v-80h600v80H40ZM80-40q-17 0-28.5-11.5T40-80v-40h600v40q0 17-11.5 28.5T600-40H80Zm282-559v80q-5 0-11-.5t-11-.5q-59 0-111.5 20T147-440h374l80 80H40q0-121 93.5-180.5T340-600q5 0 11 .5t11 .5Zm-22 159Z",
        viewBox: "0 -960 960 960",
    },
}
export const LUCIDE_ICONS = {
    towel: {
        viewBow: '0 0 24 24',
        paths: [
            "M22 7h-2",
            "M6.5 3h11A2.5 2.5 0 0 1 20 5.5V20a1 1 0 0 1-1 1h-9a1 1 0 0 1-1-1V5.5a1 1 0 0 0-5 0V17a1 1 0 0 0 1 1h4",
            "M9 7H2"
        ],
        stroke_width: "2",
        stroke_linecap: "round",
        stroke_linejoin: "round",
    },
}


export const LISTING_ICONS_BY_VALUE = {
    'Full Pool': {
        isCustomSvg: true,
        iconName: 'full-pool',
    },
    'Framed Pool': {
        isCustomSvg: true,
        iconName: 'framed-pool',
    },
    'Hot Tub': {
        isCustomSvg: true,
        iconName: 'hot-tub',
    },
    'Cold Plunge': {
        isCustomSvg: true,
        iconName: 'cold-plunge',
    },
    Chlorine: {
        isMaterialIcon: true,
        iconName: 'chlorine'
    },
    Indoor: {
        isMaterialIcon: true,
        iconName: 'house'
    },
    Outdoor: {
        isMaterialIcon: true,
        iconName: 'outdoor'
    }, 
    'Restroom in main residence': {
        isMaterialIcon: true,
        iconName: 'restroom'
    },
    'Detached restroom': {
        isMaterialIcon: true,
        iconName: 'restroom'
    },
    'Portable restroom': {  
        isMaterialIcon: true,
        iconName: 'restroom'
    },
    Saltwater: {
        isMaterialIcon: true,
        iconName: 'salinity'
    },
    Dashboard: {
        isMaterialIcon: true,
        iconName: 'dashboard'
    },
    Listings: {
        isMaterialIcon: true,
        iconName: 'listings',
    },
    Calendar: {
        isMaterialIcon: true,
        iconName: 'calendar',
    },
    Reservations: {
        isMaterialIcon: true,
        iconName: 'reservations',
    },
    Messages: {
        isMaterialIcon: true,
        iconName: 'messages',
    },
    'Account Info.': {
        isMaterialIcon: true,
        iconName: 'accountSettings',
    },
    Payments: {
        isMaterialIcon: true,
        iconName: 'payments',
    },
    Identity: {
        isMaterialIcon: true,
        iconName: 'identity',
    },

}


export function animateCopy(cmp, event) {
    const button = event.currentTarget;

    if (button) {
        const useElement = button.querySelector('use');

        if (useElement) {
            const originalHref = useElement.getAttribute('xlink:href');

            useElement.setAttribute(
                'xlink:href',
                originalHref.replace('#copy', '#check')
            );
            button.classList.add('copy-button__copied');

            clearTimeout(cmp._copyIconTimeout);

            cmp._copyIconTimeout = setTimeout(() => {
                useElement.setAttribute('xlink:href', originalHref);
                button.classList.remove('copy-button__copied');
            }, 1500);
        }
    }
}